import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Flame, LogOut, RefreshCw, ArrowDownLeft, ArrowUpRight,
  TrendingUp, Wallet, Loader2, Calendar,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import KairosisLogo from "@/components/KairosisLogo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REFRESH_MS = 30000;
const ACCENT = "#FF6D00";

function fmt(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function TradeCard({ trade: t }) {
  const positive = (t.profit_usd || 0) >= 0;
  return (
    <div className="p-4 flex items-center justify-between gap-3 border-b border-[#27272A] last:border-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-semibold text-white">{t.pair}</span>
          <span
            className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
              t.direction === "buy"
                ? "bg-[#00E676]/10 text-[#00E676]"
                : "bg-red-500/10 text-red-400"
            }`}
          >
            {t.direction}
          </span>
          {t.lot_size ? (
            <span className="text-[10px] text-gray-500">{t.lot_size} lot</span>
          ) : null}
        </div>
        <p className="text-xs text-gray-500">{fmtDateShort(t.closed_at)}</p>
      </div>
      <p className={`font-data font-bold text-base shrink-0 ${positive ? "text-[#00E676]" : "text-red-400"}`}>
        {positive ? "+" : ""}${fmt(t.profit_usd)}
      </p>
    </div>
  );
}

export default function RoarDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [acctRes, tradesRes, meRes] = await Promise.all([
        axios.get(`${API}/roar/account`, { headers: authHeaders() }),
        axios.get(`${API}/roar/trades`, { headers: authHeaders(), params: { limit: 10 } }),
        axios.get(`${API}/auth/me`, { headers: authHeaders() }),
      ]);
      setAccount(acctRes.data.account);
      setTrades(tradesRes.data.trades || []);
      setUser(meRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate("/", { replace: true, state: { accessDenied: true } });
      } else {
        toast.error("Failed to load Roar data");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSignOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-[#030303] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} />
      </div>
    );
  }

  const split = account?.profit_split ?? 70;

  return (
    <div className="min-h-[100dvh] min-h-screen bg-[#030303] text-white overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-[#FF6D00]/8 rounded-full blur-[120px]" />
      </div>

      <header className="relative z-10 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl sticky top-0">
        <div className="max-w-6xl mx-auto px-3 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <a href="/" className="flex items-center shrink-0">
              <KairosisLogo size="sm" showText={false} />
            </a>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <Flame className="w-4 h-4 text-[#FF6D00] shrink-0" />
                <span className="font-bold text-sm sm:text-base">Roar</span>
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 truncate max-w-[160px] sm:max-w-[240px]">
                {user?.name || user?.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Badge className="bg-[#FF6D00]/15 text-[#FF9100] border-[#FF6D00]/30 hidden sm:flex text-xs">
              Active
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="text-gray-400 hover:text-white h-10 w-10 p-0"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="text-gray-400 hover:text-white h-10 w-10 p-0"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-3 sm:px-8 py-4 sm:py-8 space-y-4 sm:space-y-8 pb-10">
        <Card className="bg-gradient-to-br from-[#FF6D00]/15 via-[#0A0A0A] to-[#0A0A0A] border-[#FF6D00]/25 overflow-hidden">
          <CardContent className="p-5 sm:p-10 text-center">
            <p className="text-xs sm:text-sm uppercase tracking-widest text-gray-400 mb-2">Current Balance</p>
            <p className="font-data text-3xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#FF6D00] to-[#FFAB40] bg-clip-text text-transparent break-all leading-tight">
              ${fmt(account?.balance)}
            </p>
            <div className="mt-3 sm:mt-4 flex flex-wrap justify-center gap-2">
              <Badge variant="outline" className="border-[#FF6D00]/40 text-[#FF9100] text-xs">
                Plan: {account?.plan_label || account?.plan || "—"}
              </Badge>
              <Badge variant="outline" className="border-white/10 text-gray-400 text-xs">
                {split}% profit share
              </Badge>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
          <Card className="bg-[#0A0A0A]/90 border-[#27272A]">
            <CardContent className="p-4 sm:p-5 flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#FF6D00] shrink-0 mt-0.5" />
              <div>
                <p className="text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 mb-1">Start date</p>
                <p className="text-sm sm:text-base font-semibold text-white">
                  {account?.start_date_label || "—"}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A]/90 border-[#27272A]">
            <CardContent className="p-4 sm:p-5 flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#FF9100] shrink-0 mt-0.5" />
              <div>
                <p className="text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 mb-1">Payout date</p>
                <p className="text-sm sm:text-base font-semibold text-white">
                  {account?.payout_date_label || "—"}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4">
          <Card className="bg-[#0A0A0A]/90 border-[#27272A]">
            <CardHeader className="pb-1 pt-3 px-3 sm:px-4 sm:pb-2 sm:pt-4">
              <CardTitle className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <ArrowDownLeft className="w-3 h-3 text-[#00E676] shrink-0" /> Deposit
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 sm:px-4 sm:pb-4">
              <p className="text-lg sm:text-2xl font-bold font-data text-[#00E676]">${fmt(account?.deposit)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A]/90 border-[#27272A]">
            <CardHeader className="pb-1 pt-3 px-3 sm:px-4 sm:pb-2 sm:pt-4">
              <CardTitle className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-[#FF6D00] shrink-0" /> Profit
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 sm:px-4 sm:pb-4">
              <p className="text-lg sm:text-2xl font-bold font-data text-[#FF9100]">${fmt(account?.profit)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A]/90 border-[#27272A]">
            <CardHeader className="pb-1 pt-3 px-3 sm:px-4 sm:pb-2 sm:pt-4">
              <CardTitle className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3 text-red-400 shrink-0" /> Withdrawn
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 sm:px-4 sm:pb-4">
              <p className="text-lg sm:text-2xl font-bold font-data text-red-400">${fmt(account?.withdraw)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A]/90 border-[#FF6D00]/30 col-span-2 lg:col-span-1">
            <CardHeader className="pb-1 pt-3 px-3 sm:px-4 sm:pb-2 sm:pt-4">
              <CardTitle className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <Wallet className="w-3 h-3 text-[#FF6D00] shrink-0" /> Earnings ({split}%)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 sm:px-4 sm:pb-4">
              <p className="text-lg sm:text-2xl font-bold font-data text-[#FF6D00]">${fmt(account?.earnings)}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-[#0A0A0A]/90 border-[#27272A] overflow-hidden">
          <CardHeader className="border-b border-[#27272A] px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-base sm:text-lg font-bold">Recent Trades</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {trades.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-12">No trades recorded yet.</p>
            ) : (
              <>
                <div className="md:hidden">
                  {trades.map((t) => (
                    <TradeCard key={t.id} trade={t} />
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#27272A] hover:bg-transparent">
                        <TableHead className="text-gray-500">Pair</TableHead>
                        <TableHead className="text-gray-500">Direction</TableHead>
                        <TableHead className="text-gray-500">Lot</TableHead>
                        <TableHead className="text-gray-500 text-right">P&amp;L</TableHead>
                        <TableHead className="text-gray-500 text-right">Closed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trades.map((t) => (
                        <TableRow key={t.id} className="border-[#27272A]">
                          <TableCell className="font-medium">{t.pair}</TableCell>
                          <TableCell>
                            <span
                              className={`text-xs font-semibold uppercase px-2 py-0.5 rounded ${
                                t.direction === "buy"
                                  ? "bg-[#00E676]/10 text-[#00E676]"
                                  : "bg-red-500/10 text-red-400"
                              }`}
                            >
                              {t.direction}
                            </span>
                          </TableCell>
                          <TableCell className="text-gray-400">{t.lot_size || "—"}</TableCell>
                          <TableCell
                            className={`text-right font-data font-semibold ${
                              (t.profit_usd || 0) >= 0 ? "text-[#00E676]" : "text-red-400"
                            }`}
                          >
                            {(t.profit_usd || 0) >= 0 ? "+" : ""}${fmt(t.profit_usd)}
                          </TableCell>
                          <TableCell className="text-right text-gray-500 text-sm">
                            {fmtDate(t.closed_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[10px] sm:text-xs text-gray-600 pb-4 sm:pb-8 px-2">
          Auto-refreshes every 30s · Updated {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </p>
      </main>
    </div>
  );
}
